import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import { ArrowRight } from "components/icons/ArrowRight";
import { US_UUID } from "constants/regAgent";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import React, { useRef, useState } from "react";
import { SingleValue } from "react-select";
import {
  StateRegistrationName,
  StateRegistrationNameType,
  StateRegistrationsRes,
  useAddStateRegistrationMutation,
  useGetStateRegistrationsQuery,
  useUpdateStateRegistrationMutation,
} from "store/apis/StateRegistrations";
import { useGetCountryStatesQuery } from "store/apis/teamSetting";
import { BackendError } from "types/utils/error";
import {
  ADD_STATE_EXISTING_FLOW_STEP_LABELS,
  ADD_STATE_NEW_FLOW_STEP_LABELS,
  ADD_STATE_STEP_LABELS,
  INTENT_OPTIONS,
  REGISTRATION_LABELS,
  REGISTRATION_TYPES,
  RegistrationIntent,
  SERVICE_OPTIONS,
} from "./constants";
import { RegistrationStatusName } from "store/apis/registrations";
import { RegistrationOnboarding } from "./RegistrationOnboarding";
import { StateRegistrationCard } from "./StateRegistrationCard";
import { ServiceCard } from "./StepViews";

type FormikRefValue = { submitForm: () => void; isValid: boolean } | null;

const isServiceValue = (value: string): value is StateRegistrationNameType =>
  (REGISTRATION_TYPES as string[]).includes(value);

type Step = 1 | 2 | 3;

export const AddStateRegistrationModal = ({
  isOpen,
  close,
  initialStateId,
  initialStateName,
  initialIntent,
}: {
  isOpen: boolean;
  close: () => void;
  initialStateId?: string;
  initialStateName?: string;
  initialIntent?: RegistrationIntent;
}) => {
  const { alertToast, successToast } = useToast();
  const { data: states, isLoading: statesLoading } = useGetCountryStatesQuery({
    countryId: US_UUID,
  });
  const [addStateRegistration, { isLoading: isAddingStateRegistration }] =
    useAddStateRegistrationMutation();
  const [isConfirmingNew, setIsConfirmingNew] = useState(false);
  const isAdding = isAddingStateRegistration || isConfirmingNew;
  const [updateStateRegistration] = useUpdateStateRegistrationMutation();
  const { uuid: groupId } = useCurrentGroupContext();
  const query = useQuery();
  const entityId = query.get("entity");

  const [step, setStep] = useState<Step>(
    initialStateId && initialIntent ? 3 : initialStateId ? 2 : 1
  );
  const [selectedStateId, setSelectedStateId] = useState<string>(
    initialStateId ?? ""
  );
  const [selectedStateName, setSelectedStateName] = useState<string>(
    initialStateName ?? ""
  );
  const [intent, setIntent] = useState<RegistrationIntent | null>(
    initialIntent ?? null
  );
  const [selectedServices, setSelectedServices] = useState<
    Set<StateRegistrationNameType>
  >(
    new Set([
      StateRegistrationName.SECRETARY_OF_STATE,
      StateRegistrationName.REGISTERED_AGENT,
    ])
  );
  const [onboardingStateData, setOnboardingStateData] =
    useState<StateRegistrationsRes | null>(null);
  const [stateAdded, setStateAdded] = useState(false);
  const [markingNotNeededFor, setMarkingNotNeededFor] =
    useState<StateRegistrationNameType | null>(null);
  const [revertingFor, setRevertingFor] =
    useState<StateRegistrationNameType | null>(null);
  const [notNeededServices, setNotNeededServices] = useState<
    Set<StateRegistrationNameType>
  >(new Set());
  const [everOpenedValues, setEverOpenedValues] = useState<
    Set<StateRegistrationNameType>
  >(new Set());
  const [openAccordionValues, setOpenAccordionValues] = useState<
    Set<StateRegistrationNameType>
  >(new Set());
  const sosFormikRef = useRef<FormikRefValue>(null);
  const payrollFormikRef = useRef<FormikRefValue>(null);
  const raFormikRef = useRef<FormikRefValue>(null);

  const { refetch } = useGetStateRegistrationsQuery(
    { groupId: groupId!, entityId: entityId! },
    { skip: !entityId || !groupId }
  );

  const stateOptions = states?.map(({ state_id, name }) => ({
    value: state_id,
    label: name,
  }));

  const resetState = () => {
    setStep(1);
    setSelectedStateId("");
    setSelectedStateName("");
    setIntent(null);
    setSelectedServices(
      new Set([
        StateRegistrationName.SECRETARY_OF_STATE,
        StateRegistrationName.REGISTERED_AGENT,
      ])
    );
    setOnboardingStateData(null);
    setStateAdded(false);
    setEverOpenedValues(new Set());
    setOpenAccordionValues(new Set());
    setMarkingNotNeededFor(null);
    setNotNeededServices(new Set());
  };

  const handleClose = () => {
    resetState();
    close();
  };

  const handleStep2ToStep3 = async () => {
    if (intent === "existing" && !stateAdded) {
      try {
        if (!entityId) return;
        await addStateRegistration({
          groupId,
          entityId,
          stateId: selectedStateId,
          isSelfReported: true,
        }).unwrap();
        setStateAdded(true);
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
        return;
      }
    }
    setStep(3);
  };

  const handleConfirmNew = async () => {
    if (!entityId) return;
    setIsConfirmingNew(true);
    try {
      await addStateRegistration({
        groupId,
        entityId,
        stateId: selectedStateId,
        isSelfReported: false,
      }).unwrap();

      const { data: freshRegistrations } = await refetch();
      const freshStateData = freshRegistrations?.find(
        (s) => s.state_id === selectedStateId
      );
      if (freshStateData) {
        setOnboardingStateData(freshStateData);
      } else {
        successToast({ message: `${selectedStateName} added successfully` });
        handleClose();
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    } finally {
      setIsConfirmingNew(false);
    }
  };

  const handleMarkNotNeeded = async (type: StateRegistrationNameType) => {
    if (!entityId || !groupId) return;
    setMarkingNotNeededFor(type);
    try {
      await updateStateRegistration({
        groupId,
        entityId,
        stateId: selectedStateId,
        payload: {
          data_attributes: [],
          type,
          status: "NOT_APPLICABLE",
        },
      }).unwrap();
      setNotNeededServices((prev) => new Set(prev).add(type));

      if (type === StateRegistrationName.SECRETARY_OF_STATE) {
        await updateStateRegistration({
          groupId,
          entityId,
          stateId: selectedStateId,
          payload: {
            data_attributes: [],
            type: StateRegistrationName.REGISTERED_AGENT,
            status: "NOT_APPLICABLE",
          },
        }).unwrap();
        setNotNeededServices((prev) =>
          new Set(prev).add(StateRegistrationName.REGISTERED_AGENT)
        );
      }

      successToast({
        message: `${REGISTRATION_LABELS[type]} marked as not needed`,
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    } finally {
      setMarkingNotNeededFor(null);
    }
  };

  const handleMarkAsNeeded = async (type: StateRegistrationNameType) => {
    if (!entityId || !groupId) return;
    setRevertingFor(type);
    try {
      await updateStateRegistration({
        groupId,
        entityId,
        stateId: selectedStateId,
        payload: {
          data_attributes: [],
          type,
          status: "NOT_REGISTERED",
        },
      }).unwrap();
      setNotNeededServices((prev) => {
        const next = new Set(prev);
        next.delete(type);
        return next;
      });
      successToast({
        message: `${REGISTRATION_LABELS[type]} marked as needed`,
      });

      if (type === StateRegistrationName.SECRETARY_OF_STATE) {
        await updateStateRegistration({
          groupId,
          entityId,
          stateId: selectedStateId,
          payload: {
            data_attributes: [],
            type: StateRegistrationName.REGISTERED_AGENT,
            status: "NOT_REGISTERED",
          },
        }).unwrap();
        setNotNeededServices((prev) => {
          const next = new Set(prev);
          next.delete(StateRegistrationName.REGISTERED_AGENT);
          return next;
        });
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      setRevertingFor(null);
    }
  };

  const handleSubmitExistingServices = async () => {
    try {
      await Promise.all([
        everOpenedValues.has(StateRegistrationName.SECRETARY_OF_STATE)
          ? sosFormikRef.current?.submitForm()
          : null,
        everOpenedValues.has(StateRegistrationName.PAY_ROLL)
          ? payrollFormikRef.current?.submitForm()
          : null,
        everOpenedValues.has(StateRegistrationName.SECRETARY_OF_STATE)
          ? raFormikRef.current?.submitForm()
          : null,
      ]);
      successToast({ message: `${selectedStateName} added successfully` });
      handleClose();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (intent) {
        handleStep2ToStep3();
      } else {
        setStep(2);
      }
      return;
    }
    if (step === 2) {
      handleStep2ToStep3();
      return;
    }
    if (step === 3) {
      if (intent === "new") {
        handleConfirmNew();
      } else {
        handleSubmitExistingServices();
      }
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      return;
    }
    if (step === 3) {
      setStep(initialIntent ? 1 : 2);
    }
  };

  const canProceed =
    (step === 1 && Boolean(selectedStateId)) ||
    (step === 2 && Boolean(intent)) ||
    (step === 3 && intent === "new" && selectedServices.size > 0) ||
    (step === 3 && intent !== "new");

  const nextLabel = () => {
    if (step === 3 && intent === "new") return "Add state & set up";
    if (step === 3) return "Add state";
    return "Continue";
  };

  const selectStateDescription =
    intent === "existing"
      ? "Select the registered state to add it to dashboard and start tracking."
      : "Select the state you want to register. Inkle will handle the full registration, including SOS, Registered agent and more.";

  // When the intent is already known (picked at step 2, or passed in as
  // initialIntent) there's no separate "registration status" step to show —
  // the stepper collapses to 2 pills: select state, then services.
  const isNewFlow = intent === "new" || initialIntent === "new";
  const hasKnownIntent = Boolean(intent) || Boolean(initialIntent);

  let stepperLabels: Record<number, string> = ADD_STATE_STEP_LABELS;
  let visibleSteps: readonly Step[] = [1, 2, 3];
  let activeVisibleStep: number = step;

  if (hasKnownIntent) {
    stepperLabels = isNewFlow
      ? ADD_STATE_NEW_FLOW_STEP_LABELS
      : ADD_STATE_EXISTING_FLOW_STEP_LABELS;
    visibleSteps = [1, 2];
    activeVisibleStep = Math.min(step, 2);
  }

  if (onboardingStateData) {
    return (
      <RegistrationOnboarding
        open
        onClose={handleClose}
        entryPoint="banner"
        stateData={onboardingStateData}
        initialServices={Array.from(selectedServices)}
        skipServiceSelection
      />
    );
  }

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose} modal>
      <Modal.Content useCustomOverlay size="large">
        <Modal.Header className="sm:!t-items-start sm:!t-px-4">
          <div className="t-min-w-0 t-flex-1">
            <Modal.Title>
              {initialStateId && initialIntent
                ? `Already registered in ${initialStateName}`
                : "Add new state"}
            </Modal.Title>
            {!(initialStateId && initialIntent) && (
              <Modal.Subtitle>
                <Stepper direction="horizontal" size="small" className="t-mt-4">
                  {visibleSteps.map((s, index) => (
                    <Stepper.Step
                      key={s}
                      step={s}
                      isActive={activeVisibleStep >= s}
                    >
                      <div className="t-flex t-items-center">
                        {stepperLabels[s]}
                        {index < visibleSteps.length - 1 && (
                          <ArrowRight color="currentColor" />
                        )}
                      </div>
                    </Stepper.Step>
                  ))}
                </Stepper>
              </Modal.Subtitle>
            )}
          </div>
          <Modal.Close />
        </Modal.Header>

        <Modal.Body className="sm:!t-px-4">
          <div className="t-flex t-flex-col t-gap-4">
            {/* Step 1: Select state */}
            {step === 1 && (
              <div className="t-flex t-flex-col t-gap-3">
                <span className="t-text-body-sm t-text-text-30">
                  {selectStateDescription}
                </span>
                <Combobox
                  options={stateOptions}
                  isDisabled={statesLoading}
                  isLoading={statesLoading}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  placeholder="Search and select a state..."
                  onChange={(selected) => {
                    const opt = selected as SingleValue<OptionData>;
                    setSelectedStateId(opt?.value || "");
                    setSelectedStateName((opt?.label as string) || "");
                  }}
                />
              </div>
            )}

            {/* Step 2: Registration intent */}
            {step === 2 && (
              <div className="t-flex t-flex-col t-gap-3">
                <span className="t-text-body-sm t-text-text-30">
                  Are you already operating in{" "}
                  <span className="t-font-medium t-text-text-100">
                    {selectedStateName}
                  </span>
                  ?
                </span>
                <RadioGroup.Root
                  value={intent ?? ""}
                  onValueChange={(v) => setIntent(v as RegistrationIntent)}
                >
                  <RadioGroup.Content>
                    {INTENT_OPTIONS.map((option) => (
                      <RadioGroup.TileItem
                        key={option.value}
                        value={option.value}
                        title={option.title}
                        subtext={option.description}
                      />
                    ))}
                  </RadioGroup.Content>
                </RadioGroup.Root>
              </div>
            )}

            {/* Step 3a: "new" — pick which services Inkle should register */}
            {step === 3 && intent === "new" && (
              <div className="t-flex t-flex-col t-gap-3">
                <span className="t-text-body-sm t-text-text-30">
                  Which services do you need in{" "}
                  <span className="t-font-medium t-text-text-100">
                    {selectedStateName}
                  </span>
                  ?
                </span>
                <div className="t-flex t-flex-col t-gap-2">
                  {SERVICE_OPTIONS.filter(
                    (s) => s.value !== StateRegistrationName.REGISTERED_AGENT
                  ).map((service) => {
                    const isChecked = selectedServices.has(service.value);
                    const isSOS =
                      service.value ===
                      StateRegistrationName.SECRETARY_OF_STATE;
                    const toggleService = (type: StateRegistrationNameType) =>
                      setSelectedServices((prev) => {
                        const next = new Set(prev);
                        if (next.has(type)) {
                          next.delete(type);
                          if (type === StateRegistrationName.SECRETARY_OF_STATE)
                            next.delete(StateRegistrationName.REGISTERED_AGENT);
                        } else {
                          next.add(type);
                          if (
                            type === StateRegistrationName.SECRETARY_OF_STATE &&
                            !next.has(StateRegistrationName.REGISTERED_AGENT)
                          )
                            next.add(StateRegistrationName.REGISTERED_AGENT);
                        }
                        return next;
                      });

                    return (
                      <ServiceCard
                        key={service.value}
                        type={service.value}
                        status={RegistrationStatusName.NOT_REGISTERED}
                        statusText=""
                        cartStatus={null}
                        checked={isChecked}
                        disabled={false}
                        onChange={() => toggleService(service.value)}
                        bundledItem={
                          isSOS
                            ? {
                                type: StateRegistrationName.REGISTERED_AGENT,
                                status: RegistrationStatusName.NOT_REGISTERED,
                                statusText: "",
                                cartStatus: null,
                              }
                            : undefined
                        }
                        onBundledToggle={
                          isSOS
                            ? () =>
                                toggleService(
                                  StateRegistrationName.REGISTERED_AGENT
                                )
                            : undefined
                        }
                        stateName={selectedStateName}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3b: "existing" — one independent accordion per service */}
            {step === 3 && intent === "existing" && (
              <div className="t-flex t-flex-col t-gap-5">
                <span className="t-text-body t-text-text-30">
                  Select what's set up in{" "}
                  <span className="t-font-medium t-text-text-100">
                    {selectedStateName}
                  </span>{" "}
                  and add details if needed.
                </span>
                <Accordion.Root
                  type="multiple"
                  value={Array.from(openAccordionValues)}
                  onValueChange={(values) => {
                    const serviceValues = values.filter(isServiceValue);
                    setOpenAccordionValues(new Set(serviceValues));
                    setEverOpenedValues(
                      (prev) => new Set([...prev, ...serviceValues])
                    );
                  }}
                  className="t-flex t-flex-col t-gap-2"
                >
                  {SERVICE_OPTIONS.filter(
                    (s) => s.value !== StateRegistrationName.REGISTERED_AGENT
                  ).map((service) => {
                    const isNotNeeded = notNeededServices.has(service.value);

                    if (isNotNeeded) {
                      return (
                        <div
                          key={service.value}
                          className="t-rounded-lg t-border t-border-solid t-border-neutral-10 t-flex t-items-center t-p-4 t-gap-2 sm:t-flex-col sm:t-items-start"
                        >
                          <div className="t-flex t-flex-col t-gap-0.5 t-text-left">
                            <span className="t-text-subtext-sm t-font-medium t-text-text-30">
                              {service.label}
                            </span>
                            <span className="t-text-body-sm t-text-text-30">
                              {service.alreadyRegisteredDescription}
                            </span>
                          </div>
                          <div className="t-ml-auto sm:t-ml-0">
                            <Button
                              size="xsmall"
                              customType="ghost"
                              isLoading={revertingFor === service.value}
                              onClick={() => handleMarkAsNeeded(service.value)}
                            >
                              Mark as needed
                            </Button>
                          </div>
                        </div>
                      );
                    }

                    const isSOS =
                      service.value ===
                      StateRegistrationName.SECRETARY_OF_STATE;
                    const formikRef = isSOS ? sosFormikRef : payrollFormikRef;

                    return (
                      <Accordion.Item key={service.value} value={service.value}>
                        <div className="t-flex t-items-center t-gap-2 t-pr-4 sm:t-flex-col sm:t-items-stretch sm:t-gap-0 sm:t-pr-0">
                          <Accordion.Trigger className="!t-pr-0 after:!t-border-b-0 sm:!t-pr-4">
                            <span className="t-flex t-flex-col t-gap-0.5 t-text-left">
                              <span className="t-text-subtext-sm t-font-medium t-text-text-100">
                                {service.label}
                              </span>
                              <span className="t-text-body-sm t-text-text-30 t-font-normal t-normal-case">
                                {service.alreadyRegisteredDescription}
                              </span>
                            </span>
                          </Accordion.Trigger>
                          <Button
                            size="xsmall"
                            customType="secondary"
                            className="t-shrink-0 sm:t-mx-4 sm:t-mb-4 sm:t-self-start"
                            isLoading={markingNotNeededFor === service.value}
                            onClick={() => handleMarkNotNeeded(service.value)}
                          >
                            Not applicable
                          </Button>
                        </div>
                        <Accordion.Content className="!t-pt-0 sm:!t-px-2">
                          <StateRegistrationCard
                            stateId={selectedStateId}
                            stateType={service.value}
                            stateName={selectedStateName}
                            flat
                            startInEditMode
                            hideAlreadyRegisteredLabel
                            hideInnerBorder
                            hideBottomActions
                            onLetInkleHandle={() => {}}
                            formikRef={formikRef}
                          />
                          {isSOS && (
                            <div className="t-border-0 t-border-t t-border-solid t-border-neutral-10">
                              <div className="t-px-4 t-pt-4 t-pb-2">
                                <span className="t-text-subtext-sm t-font-medium t-text-text-100">
                                  Registered Agent · Included
                                </span>
                              </div>
                              <StateRegistrationCard
                                stateId={selectedStateId}
                                stateType={
                                  StateRegistrationName.REGISTERED_AGENT
                                }
                                stateName={selectedStateName}
                                flat
                                startInEditMode
                                hideAlreadyRegisteredLabel
                                hideInnerBorder
                                hideBottomActions
                                onLetInkleHandle={() => {}}
                                formikRef={raFormikRef}
                              />
                            </div>
                          )}
                        </Accordion.Content>
                      </Accordion.Item>
                    );
                  })}
                </Accordion.Root>
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer className="t-flex t-items-center t-justify-between sm:!t-px-4">
          {step > 1 && !(initialStateId && initialIntent) ? (
            <Button customType="ghost" onClick={handleBack}>
              Back
            </Button>
          ) : (
            <span />
          )}
          <div className="t-flex t-gap-2 t-items-center">
            <Modal.RawClose asChild>
              <Button customType="secondary" onClick={handleClose}>
                Cancel
              </Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              onClick={handleNext}
              disabled={!canProceed || isAdding}
              isLoading={isAdding}
            >
              {nextLabel()}
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
